import { EInsideElement } from "$src/CGS/domain/dto/core/EInsideElement";
import { EBoneType } from "$src/CGS/domain/dto/core/bones/EBoneType";
import { Object3D, Vector3 } from "three";
import { ApplianceInsideMetadata } from "../Metadata/ApplianceInsideMetadata";
import { InsideElementMetadata } from "../Metadata/InsideElementMetadata";
import { InsideContainerGenerator } from "./InsideContainerGenerator";
import { BaseInsideElementGenerator } from "./BaseInsideElementGenerator";
import { ApplianceApertureBoneObject } from "../Objects/ApplianceApertureBoneObject";
import RangeLength from "$src/CGS/domain/dto/core/Length/RangeLength";

export class ApplianceInsideGenerator extends BaseInsideElementGenerator {
    private _applianceInsideMetadata: ApplianceInsideMetadata;

    constructor(insideContainerGenerator: InsideContainerGenerator, insideElementMetadata: InsideElementMetadata) {
        super(insideContainerGenerator, insideElementMetadata);

        if (!(insideElementMetadata instanceof ApplianceInsideMetadata)) {
            throw new Error(`${insideElementMetadata} is not an instance of ApplianceInsideMetadata`);
        }

        this._applianceInsideMetadata = insideElementMetadata as ApplianceInsideMetadata;
    }

    private getBoneName(): string {
        switch (this.insideElementMetadata.Type) {
            case EInsideElement.A:
                return "BOT_oven_placement_BONE";
            case EInsideElement.F:
                return "BOT_fridge_placement_BONE";
            case EInsideElement.DW:
                return "BOT_dishwasher_placement_BONE";
            case EInsideElement.W:
                return "BOT_washingmachine_placement_BONE";
            default:
                return "";
        }
    }

    private getBoneType(): EBoneType {
        switch (this.insideElementMetadata.Type) {
            case EInsideElement.A:
                return EBoneType.Oven;
            case EInsideElement.F:
                return EBoneType.Fridge;
            case EInsideElement.DW:
                return EBoneType.Dishwasher;
            case EInsideElement.W:
                return EBoneType.WashingMachine;
            default:
                throw new Error(`No bone pattern matches the appliance recess "${this.insideElementMetadata.Type}"`);
        }
    }

    public build(): void {
        const name: string = this.getBoneName();
        const gameObject: Object3D = new Object3D();

        gameObject.name = name;

        this.insideContainerGenerator.cabinet.Placement?.add(gameObject);

        const outline: Vector3[] = this.insideContainerGenerator.cabinetShapeGenerator.getOutline();

        const v1: Vector3 = outline[this.insideContainerGenerator.cabinetShapeGenerator.getFrontFaceIndexInOutline(this.insideContainerGenerator.insideContainerMetadata.StartPlacement.FaceIndex, false)];
        const v2: Vector3 = outline[this.insideContainerGenerator.cabinetShapeGenerator.getFrontFaceIndexInOutline(this.insideContainerGenerator.insideContainerMetadata.StartPlacement.FaceIndex, true)];

        const tangent: Vector3 = new Vector3().subVectors(v2, v1).normalize();
        const normal: Vector3 = new Vector3().crossVectors(tangent, new Vector3(0, 1, 0));

        const v: Vector3 = new Vector3().addVectors(v1, tangent.clone().multiplyScalar(this._applianceInsideMetadata.BonePlacement?.Offset?.meter ?? 0));

        gameObject.position.set(v.x, this.insideElementMetadata.Anchor.y, v.z);
        gameObject.quaternion.setFromUnitVectors(normal.clone().negate(), new Vector3(0, 1, 0));

        const bone: ApplianceApertureBoneObject = new ApplianceApertureBoneObject();
        bone.Type = this.getBoneType();
        bone.ApertureIdentifier = this._applianceInsideMetadata.ApplianceApertureIdentifier;
        bone.PossibleHeight = this._applianceInsideMetadata.Height as RangeLength;

        this.insideContainerGenerator.cabinet.Bones.push(bone);
    }
}
